import PropTypes from 'prop-types';
import { useRef, useEffect, useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import normalizeAssertionBaseUrl from 'util/normalizeAssertionBaseUrl';
import { buildPalGuestPaymentPopupFeatures } from './popupConstants';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  errorContainer: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  errorText: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  statusText: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
  },
  popupBlockedText: {
    color: theme.palette.warning.main,
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
  },
}));

/** How often to pull focus back to the payment popup (ms). */
const PAYMENT_POPUP_FOREGROUND_MS = 250;

function focusPaymentWindow(popup) {
  if (!popup || popup.closed) return;
  try {
    popup.focus();
  } catch (_e) {
    // cross-origin or closed during focus
  }
}

const PaymentIframe = ({
  jwt,
  fspId,
  assertionBaseUrl,
  ifpAssertionVariant,
  onError,
  onPopupClosed,
  onPopupReady,
  preOpenedPopup,
}) => {
  const classes = useStyles();
  const popupRef = useRef(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Opening payment window...');
  const [isPopupBlocked, setIsPopupBlocked] = useState(false);
  const popupGuardKey = 'PAYRAILZ_PAL_GUEST_PAYMENT_POPUP_GUARD';

  const normalizedAssertionBaseUrl = useMemo(
    () => normalizeAssertionBaseUrl(assertionBaseUrl),
    [assertionBaseUrl]
  );

  useEffect(() => {
    const clearGuard = () => {
      globalThis[popupGuardKey] = false;
    };

    if (globalThis[popupGuardKey]) {
      return undefined;
    }
    globalThis[popupGuardKey] = true;
    if (!jwt || !fspId || !assertionBaseUrl) {
      const errorMessage = 'Missing required parameters for payment popup';
      setError(errorMessage);
      if (onError) onError(errorMessage);
      clearGuard();
      return undefined;
    }

    const queryParams = new URLSearchParams({
      jwt,
      fspId: String(fspId),
      assertionBaseUrl: normalizedAssertionBaseUrl,
    });
    queryParams.set('ifpVariant', ifpAssertionVariant);

    const palProxyBaseUrl = process.env.REACT_APP_PAL_PROXY_BASE_URL;
    if (palProxyBaseUrl) {
      queryParams.set('proxyBaseUrl', palProxyBaseUrl);
    }

    const popupUrl = `${
      window.location.origin
    }/palGuestPaymentIframe.html?${queryParams.toString()}`;

    const popupFeatures = buildPalGuestPaymentPopupFeatures();

    let popup;

    if (preOpenedPopup && !preOpenedPopup.closed) {
      try {
        preOpenedPopup.location.href = popupUrl;
      } catch (_navError) {
        setError('Failed to navigate payment window');
        if (onError) onError('Failed to navigate payment window');
        clearGuard();
        return undefined;
      }
      popup = preOpenedPopup;
    } else {
      popup = window.open(popupUrl, 'PALGuestPayment', popupFeatures);

      if (!popup || popup.closed || popup.closed === undefined) {
        setIsPopupBlocked(true);
        setError(
          'Popup window was blocked. Please allow popups for this site and try again.'
        );
        if (onError) onError('Popup window was blocked');
        clearGuard();
        return undefined;
      }
    }

    popupRef.current = popup;
    if (onPopupReady) {
      onPopupReady(popup);
    }
    focusPaymentWindow(popup);
    setStatus('Payment window opened');

    const shouldSkipRefocusBecauseDialogFocused = () => {
      const active = document.activeElement;
      if (!active || !active.closest) return false;
      return Boolean(active.closest('[role="dialog"]'));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (shouldSkipRefocusBecauseDialogFocused()) return;
      focusPaymentWindow(popup);
    };

    const handleOpenerBlur = () => {
      if (shouldSkipRefocusBecauseDialogFocused()) return;
      window.setTimeout(() => focusPaymentWindow(popup), 0);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleOpenerBlur);

    const mainInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(mainInterval);
        globalThis[popupGuardKey] = false;
        if (onPopupReady) {
          onPopupReady(null);
        }
        if (onPopupClosed) onPopupClosed();
        return;
      }
      if (!shouldSkipRefocusBecauseDialogFocused()) {
        focusPaymentWindow(popup);
      }
    }, PAYMENT_POPUP_FOREGROUND_MS);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleOpenerBlur);
      clearInterval(mainInterval);
      clearGuard();
      if (onPopupReady) {
        onPopupReady(null);
      }
    };
  }, [
    jwt,
    fspId,
    assertionBaseUrl,
    normalizedAssertionBaseUrl,
    ifpAssertionVariant,
    onError,
    onPopupClosed,
    onPopupReady,
    preOpenedPopup,
  ]);

  if (error) {
    return (
      <div className={classes.errorContainer}>
        <div className={classes.errorText}>{error}</div>
        {isPopupBlocked && (
          <div className={classes.popupBlockedText}>
            <strong>Popup Blocked</strong>
            <p>Your browser blocked the payment window. Please:</p>
            <ul style={{ textAlign: 'left', display: 'inline-block' }}>
              <li>Allow popups for this site in your browser settings</li>
              <li>Try clicking the payment button again</li>
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.statusText}>{status}</div>
    </div>
  );
};

PaymentIframe.propTypes = {
  jwt: PropTypes.string.isRequired,
  fspId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  assertionBaseUrl: PropTypes.string.isRequired,
  ifpAssertionVariant: PropTypes.oneOf(['guest', 'enrolled']),
  onError: PropTypes.func,
  onPopupClosed: PropTypes.func,
  onPopupReady: PropTypes.func,
  preOpenedPopup: PropTypes.shape({
    closed: PropTypes.bool,
    close: PropTypes.func,
    location: PropTypes.shape({
      href: PropTypes.string,
    }),
  }),
};

PaymentIframe.defaultProps = {
  ifpAssertionVariant: 'guest',
  onError: null,
  onPopupClosed: null,
  onPopupReady: null,
  preOpenedPopup: null,
};

export default PaymentIframe;
